import type { IDatePicker, Season, WeekDay } from '@bridebook/models/source/models/Weddings.types';
import { getSeasonMonth } from '@bridebook/toolbox/src/datepicker/get-season-month';
import gazetteer, { CountryCodes } from '@bridebook/toolbox/src/gazetteer';
import { SEASONS, WEEK_DAYS } from '@bridebook/web/lib/datepicker/constants';

/**
 * The single date shape for the planner. The app stores year, month or season, and day or
 * weekday; the picker's weekday choices are Monday to Thursday, Friday, Saturday and Sunday.
 * All planner code reads and writes this shape, so none of it needs to know the picker.
 *
 * The picker cannot store "weekend": it is saved as Saturday and reads back as Saturday.
 */
export type { Season };

export const WEEKDAY_PREFERENCES = ['midweek', 'fri', 'sat', 'sun', 'weekend'] as const;
export type WeekdayPreference = (typeof WEEKDAY_PREFERENCES)[number];

// The stored shape the app's date write takes: the picker type without a list of months,
// which the write does not accept.
export type StoredWeddingDate = {
  y: number;
  m: number | Season | null;
  d: number | WeekDay | WeekDay[] | null;
};

export type WeddingDate = {
  year: number;
  // 1-indexed. Never set together with a season.
  month?: number;
  season?: Season;
  // Day of the month. Never set together with a weekday.
  day?: number;
  weekday?: WeekdayPreference;
};

const MIDWEEK: readonly WeekDay[] = ['mon', 'tue', 'wed', 'thu'];
const GB = gazetteer.getMarketByCountry(CountryCodes.GB);

export const isSeason = (value: unknown): value is Season =>
  typeof value === 'string' && (SEASONS as readonly string[]).includes(value);

export const isWeekdayPreference = (value: unknown): value is WeekdayPreference =>
  typeof value === 'string' && (WEEKDAY_PREFERENCES as readonly string[]).includes(value);

// The picker's stored form of each weekday preference.
const storedWeekday = (weekday: WeekdayPreference | undefined): StoredWeddingDate['d'] => {
  switch (weekday) {
    case 'midweek':
      return [...MIDWEEK];
    case 'weekend':
      return 'sat';
    case 'fri':
    case 'sat':
    case 'sun':
      return weekday;
    default:
      return null;
  }
};

// The stored shape the app's date write takes.
export const weddingDateToPicker = (date: WeddingDate): StoredWeddingDate => ({
  y: date.year,
  m: date.month != null ? date.month - 1 : (date.season ?? null),
  d: date.day != null ? date.day : storedWeekday(date.weekday),
});

/**
 * The stored shape read back. Null without a year, because the checklist needs one. Any
 * list of weekdays reads as midweek, as the picker reads it.
 */
export const weddingDateFromPicker = (
  picker: IDatePicker | null | undefined,
): WeddingDate | null => {
  if (!picker || typeof picker.y !== 'number') return null;
  const date: WeddingDate = { year: picker.y };
  if (typeof picker.m === 'number') {
    date.month = picker.m + 1;
  } else if (isSeason(picker.m)) {
    date.season = picker.m;
  }
  if (typeof picker.d === 'number') {
    date.day = picker.d;
  } else if (Array.isArray(picker.d)) {
    date.weekday = 'midweek';
  } else if (picker.d === 'fri' || picker.d === 'sat' || picker.d === 'sun') {
    date.weekday = picker.d;
  } else if (typeof picker.d === 'string' && (WEEK_DAYS as readonly string[]).includes(picker.d)) {
    date.weekday = 'midweek';
  }
  return date;
};

/**
 * The profile string: `2028`, `2028-06`, `2028-06-10`, `2028-summer`, `2028-06-sat`,
 * `2028-summer-midweek`. Zero-padded, so a less exact date is a prefix of a more exact one.
 */
export const formatWeddingDate = (date: WeddingDate): string => {
  const parts = [String(date.year)];
  if (date.month != null) {
    parts.push(String(date.month).padStart(2, '0'));
  } else if (date.season) {
    parts.push(date.season);
  }
  if (date.day != null) {
    parts.push(String(date.day).padStart(2, '0'));
  } else if (date.weekday) {
    parts.push(date.weekday);
  }
  return parts.join('-');
};

// Whether a 1-indexed month falls in a season, on the app's own three-month seasons.
const monthInSeason = (month: number, season: Season): boolean =>
  (month - 1 - getSeasonMonth(season, GB) + 12) % 12 < 3;

/**
 * Whether a new date contradicts the one on file instead of making it more exact. A
 * different year, month or day contradicts, and so does a month outside the season on file.
 * Adding a day, a month within the season, or a day in place of a weekday makes it more
 * exact. A contradiction can be a change of plan, or another event's date taken as the
 * wedding's, so callers treat it differently.
 */
export const contradictsWeddingDate = (before: WeddingDate, next: WeddingDate): boolean => {
  if (before.year !== next.year) return true;
  if (before.month != null && next.month != null && before.month !== next.month) return true;
  if (before.season && next.season && before.season !== next.season) return true;
  if (before.season && next.month != null && !monthInSeason(next.month, before.season)) {
    return true;
  }
  if (before.month != null && next.season && !monthInSeason(before.month, next.season)) {
    return true;
  }
  return before.day != null && next.day != null && before.day !== next.day;
};

// The profile string read back. Null when it has no year.
export const parseWeddingDate = (text: string | null | undefined): WeddingDate | null => {
  if (!text) return null;
  const [first, second, third] = text.split('-');
  const year = Number.parseInt(first ?? '', 10);
  if (!Number.isFinite(year)) return null;
  const date: WeddingDate = { year };
  const month = Number.parseInt(second ?? '', 10);
  if (Number.isFinite(month) && month >= 1 && month <= 12) {
    date.month = month;
  } else if (isSeason(second)) {
    date.season = second;
  } else if (isWeekdayPreference(second)) {
    date.weekday = second;
    return date;
  }
  const day = Number.parseInt(third ?? '', 10);
  if (Number.isFinite(day) && day >= 1 && day <= 31) {
    date.day = day;
  } else if (isWeekdayPreference(third)) {
    date.weekday = third;
  }
  return date;
};

export const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const WEEKDAY_WORDS: Record<WeekdayPreference, string> = {
  midweek: 'midweek',
  fri: 'a Friday',
  sat: 'a Saturday',
  sun: 'a Sunday',
  weekend: 'a weekend',
};

/**
 * The date in words: "10 June 2028", "June 2028", "summer 2028", "a Saturday in June 2028",
 * "midweek in summer 2028". Lower case as in a sentence, so a card can capitalise it when it
 * stands alone.
 */
export const describeWeddingDate = (date: WeddingDate): string => {
  const when =
    date.month != null
      ? `${MONTHS[date.month - 1]} ${date.year}`
      : date.season
        ? `${date.season} ${date.year}`
        : String(date.year);
  if (date.day != null && date.month != null) {
    return `${date.day} ${when}`;
  }
  if (date.weekday) {
    return `${WEEKDAY_WORDS[date.weekday]} in ${when}`;
  }
  return when;
};

/**
 * The calendar point for a date that may be a season or a weekday preference. A season uses
 * its first month, as elsewhere in the app; a weekday preference uses the first of the month.
 * A null month means only the year is known.
 */
export const weddingDateAnchor = (
  date: WeddingDate,
): { year: number; month: number | null; day: number } => ({
  year: date.year,
  month: date.month ?? (date.season ? getSeasonMonth(date.season, GB) + 1 : null),
  day: date.day ?? 1,
});
